import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";

const useStyles = makeStyles(theme => ({
	field: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},
}));

// Aceita 10, 11, 12 ou 13 dígitos (com ou sem DDI 55, com ou sem o 9 adicional).
const isValidPhoneInput = (raw) => {
	const digits = String(raw || "").replace(/\D/g, "");
	if (digits.length === 12 || digits.length === 13) {
		return digits.startsWith("55");
	}
	return digits.length === 10 || digits.length === 11;
};

const applyPhoneMask = (value) => {
	const digits = String(value || "").replace(/\D/g, "").slice(0, 13);

	// Com DDI 55: +55 (XX) 9XXXX-XXXX
	if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) {
		const ddd = digits.substring(2, 4);
		const rest = digits.substring(4);
		if (!ddd) return `+55`;
		if (rest.length <= 4) return `+55 (${ddd}) ${rest}`;
		if (rest.length <= 8) return `+55 (${ddd}) ${rest.substring(0, 4)}-${rest.substring(4)}`;
		return `+55 (${ddd}) ${rest.substring(0, rest.length - 4)}-${rest.substring(rest.length - 4)}`;
	}

	// Sem DDI: (XX) 9XXXX-XXXX
	if (digits.length <= 2) return digits;
	if (digits.length <= 6) return `(${digits.substring(0, 2)}) ${digits.substring(2)}`;
	if (digits.length <= 10) return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
	return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
};

const NewTicketModal = ({ modalOpen, whatsApps, whatsappIdPadrao, onClose }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const [loading, setLoading] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState("");
	const [name, setName] = useState("");
	const [number, setNumber] = useState("");
	const [numberError, setNumberError] = useState("");
	const [nameError, setNameError] = useState("");

	// A conexão vem escolhida conforme o filtro da tela de conversas
	// (whatsappIdPadrao); sem filtro, a primeira da lista. Antes era sempre a
	// primeira da lista e a escolha da abertura anterior era mantida — quem filtrava
	// uma conexão e clicava em "Novo lead" abria o lead em outra conexão.
	// Reaplica a cada abertura (o ref zera no fechamento) e tenta de novo enquanto a
	// lista de conexões não chegou.
	const padraoAplicado = useRef(false);
	useEffect(() => {
		if (!modalOpen) {
			padraoAplicado.current = false;
			return;
		}
		if (padraoAplicado.current) return;
		const disponiveis = whatsApps || [];
		if (disponiveis.length === 0) return;
		const temPadrao = disponiveis.some((w) => w.id === whatsappIdPadrao);
		setSelectedWhatsApp(temPadrao ? whatsappIdPadrao : disponiveis[0].id);
		padraoAplicado.current = true;
	}, [modalOpen, whatsApps, whatsappIdPadrao]);

	const resetState = () => {
		setName("");
		setNumber("");
		setNumberError("");
		setNameError("");
	};

	const handleClose = () => {
		onClose();
		resetState();
	};

	const handleNumberChange = (e) => {
		setNumber(applyPhoneMask(e.target.value));
		if (numberError) setNumberError("");
	};

	const handleNameChange = (e) => {
		setName(e.target.value);
		if (nameError) setNameError("");
	};

	const handleSave = async () => {
		let hasError = false;
		if (!name.trim()) {
			setNameError("Informe o nome do contato.");
			hasError = true;
		}
		if (!isValidPhoneInput(number)) {
			setNumberError(i18n.t("newTicketModal.invalidNumber"));
			hasError = true;
		}
		if (!selectedWhatsApp) {
			toast.error(i18n.t("backendErrors.ERR_NO_WAPP_SELECTED"));
			hasError = true;
		}
		if (hasError) return;

		setLoading(true);
		try {
			const { data } = await api.post("/ticketsNovoLead", {
				name: name.trim(),
				number,
				whatsappId: selectedWhatsApp,
			});
			if (data.isExisting) {
				toast.info(i18n.t("newTicketModal.infoExisting"));
			} else {
				toast.success(i18n.t("newTicketModal.successNew"));
			}
			history.push(`/tickets/${data.ticket.id}`);
			handleClose();
		} catch (err) {
			const code = err.response?.data?.message || err.response?.data?.error;
			const numberCodes = [
				"ERR_NUMERO_INVALIDO",
				"ERR_WAPP_INVALID_CONTACT",
				"ERR_WAPP_CHECK_CONTACT",
			];
			if (code === "ERR_NOME_OBRIGATORIO") {
				setNameError(i18n.t("backendErrors.ERR_NOME_OBRIGATORIO"));
			} else if (numberCodes.includes(code)) {
				setNumberError(i18n.t(`backendErrors.${code}`));
			} else {
				toastError(err);
			}
		}
		setLoading(false);
	};

	return (
		<Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="xs">
			<DialogTitle id="form-dialog-title">
				{i18n.t("newTicketModal.title")}
			</DialogTitle>
			<DialogContent dividers>
				<FormControl variant="outlined" margin="dense" className={classes.field}>
					<InputLabel id="newticket-connection-label">
						{i18n.t("newTicketModal.connection")}
					</InputLabel>
					<Select
						labelId="newticket-connection-label"
						value={selectedWhatsApp}
						onChange={(e) => setSelectedWhatsApp(e.target.value)}
						label={i18n.t("newTicketModal.connection")}
					>
						{whatsApps?.length > 0 &&
							whatsApps.map((whatsApp) => (
								<MenuItem key={whatsApp.id} value={whatsApp.id}>
									{whatsApp.name}
								</MenuItem>
							))}
					</Select>
				</FormControl>
				<TextField
					label={i18n.t("newTicketModal.name")}
					value={name}
					onChange={handleNameChange}
					error={Boolean(nameError)}
					helperText={nameError}
					variant="outlined"
					margin="dense"
					autoFocus
					className={classes.field}
				/>
				<TextField
					label={i18n.t("newTicketModal.number")}
					value={number}
					onChange={handleNumberChange}
					error={Boolean(numberError)}
					helperText={numberError}
					placeholder="(99) 99999-9999"
					variant="outlined"
					margin="dense"
					inputProps={{ maxLength: 20 }}
					className={classes.field}
					onKeyPress={(e) => {
						if (e.key === "Enter" && !loading) handleSave();
					}}
				/>
			</DialogContent>
			<DialogActions>
				<BotaoCancelar onClick={handleClose} disabled={loading}>
					{i18n.t("newTicketModal.buttons.cancel")}
				</BotaoCancelar>
				<BotaoConfirmar type="button" onClick={handleSave} loading={loading}>
					{i18n.t("newTicketModal.buttons.ok")}
				</BotaoConfirmar>
			</DialogActions>
		</Dialog>
	);
};

export default NewTicketModal;
